import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../ui/alert-dialog';
import type { NavigationItem } from '../Navigation';
import {
  type CampaignReferenceData,
  type TargetProfileOption,
  useCampaignApi,
  type CreateTargetProfileOptionRequest,
  type UpdateTargetProfileOptionRequest
} from '../../services/apiService';
import { toast } from 'sonner';
import { ArrowLeft, Plus, RefreshCw, Trash2 } from 'lucide-react';

type AudienceType = 'B2B' | 'B2C';

interface TargetProfileManagementProps {
  onNavigate: (view: NavigationItem) => void;
}

interface FlattenedProfileOption {
  id: number;
  label: string;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    const match = error.message.match(/API Error: \d+ - (.*)$/);
    if (match?.[1]) {
      try {
        const parsed = JSON.parse(match[1]);
        if (typeof parsed === 'string') {
          return parsed;
        }
        if (parsed && typeof parsed.message === 'string') {
          return parsed.message;
        }
      } catch {
        return match[1];
      }
    }
    return error.message;
  }
  return fallback;
}

function flattenTargetProfiles(options: TargetProfileOption[], depth = 0): FlattenedProfileOption[] {
  const entries: FlattenedProfileOption[] = [];
  const prefix = depth > 0 ? `${'— '.repeat(depth)}` : '';

  for (const option of options) {
    entries.push({ id: option.id, label: `${prefix}${option.name}` });
    if (option.children?.length) {
      entries.push(...flattenTargetProfiles(option.children, depth + 1));
    }
  }

  return entries;
}

function countTargetProfiles(options: TargetProfileOption[]): number {
  return options.reduce((total, option) => total + 1 + countTargetProfiles(option.children ?? []), 0);
}

function findTargetProfileById(options: TargetProfileOption[], id: number): TargetProfileOption | null {
  for (const option of options) {
    if (option.id === id) {
      return option;
    }
    const childMatch = findTargetProfileById(option.children ?? [], id);
    if (childMatch) {
      return childMatch;
    }
  }
  return null;
}

export function TargetProfileManagement({ onNavigate }: TargetProfileManagementProps) {
  const campaignApi = useCampaignApi();

  const [referenceData, setReferenceData] = useState<CampaignReferenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [selectedAudience, setSelectedAudience] = useState<AudienceType>('B2B');
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    parentId: null as number | null
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const [editForm, setEditForm] = useState({
    name: '',
    description: ''
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const refreshReferenceData = useCallback(async (): Promise<boolean> => {
    try {
      const data = await campaignApi.getReferenceData();
      setReferenceData(data);
      setLoadError(null);
      return true;
    } catch (error) {
      setLoadError(extractErrorMessage(error, 'Failed to load target profiles.'));
      return false;
    }
  }, [campaignApi]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await refreshReferenceData();
      setLoading(false);
    };

    load();
  }, [refreshReferenceData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const success = await refreshReferenceData();
    setIsRefreshing(false);
    if (success) {
      toast.success('Target profiles refreshed.');
    } else {
      toast.error('Unable to refresh target profiles.');
    }
  };

  const allProfiles = useMemo(() => {
    if (!referenceData) {
      return [];
    }
    return [
      ...(referenceData.b2bTargetProfiles ?? []),
      ...(referenceData.b2cTargetProfiles ?? [])
    ];
  }, [referenceData]);

  const selectedProfile = useMemo(() => {
    if (selectedProfileId == null || allProfiles.length === 0) {
      return null;
    }
    return findTargetProfileById(allProfiles, selectedProfileId);
  }, [allProfiles, selectedProfileId]);

  useEffect(() => {
    if (selectedProfile && selectedProfile.audience.toUpperCase() !== selectedAudience) {
      setSelectedProfileId(null);
    }
  }, [selectedAudience, selectedProfile]);

  useEffect(() => {
    if (selectedProfile) {
      setEditForm({
        name: selectedProfile.name,
        description: selectedProfile.description ?? ''
      });
      setEditError(null);
    } else {
      setEditForm({ name: '', description: '' });
    }
  }, [selectedProfile]);

  useEffect(() => {
    setCreateForm(prev => ({ ...prev, parentId: null }));
  }, [selectedAudience]);

  const currentProfiles = useMemo(() => {
    if (!referenceData) {
      return [];
    }
    return selectedAudience === 'B2B'
      ? referenceData.b2bTargetProfiles ?? []
      : referenceData.b2cTargetProfiles ?? [];
  }, [referenceData, selectedAudience]);

  const flattenedParentOptions = useMemo(() => flattenTargetProfiles(currentProfiles), [currentProfiles]);

  const totalB2BProfiles = useMemo(
    () => countTargetProfiles(referenceData?.b2bTargetProfiles ?? []),
    [referenceData]
  );
  const totalB2CProfiles = useMemo(
    () => countTargetProfiles(referenceData?.b2cTargetProfiles ?? []),
    [referenceData]
  );

  const handleAudienceChange = (audience: AudienceType) => {
    setSelectedAudience(audience);
    setSelectedProfileId(null);
    setCreateError(null);
  };

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateError(null);

    const trimmedName = createForm.name.trim();
    if (trimmedName.length === 0) {
      setCreateError('Name is required.');
      return;
    }

    const payload: CreateTargetProfileOptionRequest = {
      name: trimmedName,
      audience: selectedAudience,
      description: createForm.description.trim().length > 0 ? createForm.description.trim() : undefined,
      parentId: createForm.parentId ?? undefined
    };

    setIsCreating(true);
    try {
      await campaignApi.createTargetProfile(payload);
      toast.success('Target profile created.');
      setCreateForm({ name: '', description: '', parentId: null });
      await refreshReferenceData();
    } catch (error) {
      setCreateError(extractErrorMessage(error, 'Failed to create target profile.'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedProfile) {
      return;
    }

    setEditError(null);

    const trimmedName = editForm.name.trim();
    if (trimmedName.length === 0) {
      setEditError('Name is required.');
      return;
    }

    const updates: UpdateTargetProfileOptionRequest = {};

    if (trimmedName !== selectedProfile.name) {
      updates.name = trimmedName;
    }

    const normalizedDescription = editForm.description.trim();
    const currentDescription = selectedProfile.description ?? '';
    if (normalizedDescription !== currentDescription.trim()) {
      updates.description = normalizedDescription.length === 0 ? '' : normalizedDescription;
    }

    if (Object.keys(updates).length === 0) {
      setEditError('No changes to save.');
      return;
    }

    setIsUpdating(true);
    try {
      await campaignApi.updateTargetProfile(selectedProfile.id, updates);
      toast.success('Target profile updated.');
      await refreshReferenceData();
    } catch (error) {
      setEditError(extractErrorMessage(error, 'Failed to update target profile.'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfile) {
      return;
    }

    setEditError(null);
    setIsDeleting(true);
    try {
      await campaignApi.deleteTargetProfile(selectedProfile.id);
      toast.success('Target profile deleted.');
      setSelectedProfileId(null);
      await refreshReferenceData();
    } catch (error) {
      setEditError(extractErrorMessage(error, 'Failed to delete target profile.'));
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const renderTargetProfileTree = (options: TargetProfileOption[], depth = 0) => {
    return options.map(option => (
      <div key={option.id} className="space-y-1">
        <div
          className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${
            selectedProfileId === option.id ? 'border-primary bg-primary/10' : 'border-border bg-background'
          }`}
          style={{ marginLeft: depth * 16 }}
        >
          <button
            type="button"
            onClick={() => setSelectedProfileId(option.id)}
            className="text-left flex-1"
          >
            <div className="text-sm font-medium text-foreground">{option.name}</div>
            {option.description && (
              <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{option.description}</p>
            )}
          </button>
          <div className="flex flex-col items-end gap-1">
            {selectedProfileId === option.id && <Badge variant="secondary">Selected</Badge>}
            <Button
              type="button"
              variant={createForm.parentId === option.id ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setCreateForm(prev => ({ ...prev, parentId: option.id }))}
            >
              {createForm.parentId === option.id ? 'Parent' : 'Set parent'}
            </Button>
          </div>
        </div>
        {option.children && option.children.length > 0 && (
          <div>{renderTargetProfileTree(option.children, depth + 1)}</div>
        )}
      </div>
    ));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => onNavigate('campaign-dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Campaign Dashboard
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleRefresh}
          disabled={loading || isRefreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Target Profile Management</CardTitle>
          <CardDescription>
            Create, update, and remove B2B and B2C target profiles used by marketing campaigns. B2B profiles: {totalB2BProfiles} • B2C profiles: {totalB2CProfiles}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Audience:</span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={selectedAudience === 'B2B' ? 'default' : 'outline'}
                onClick={() => handleAudienceChange('B2B')}
              >
                B2B Profiles
              </Button>
              <Button
                type="button"
                variant={selectedAudience === 'B2C' ? 'default' : 'outline'}
                onClick={() => handleAudienceChange('B2C')}
              >
                B2C Profiles
              </Button>
            </div>
          </div>

          {loadError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{loadError}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Existing Profiles</h3>
                <p className="text-xs text-muted-foreground">
                  Select a profile to edit or remove it. Use "Set parent" to quickly assign a parent when creating a new profile.
                </p>
              </div>
              <div className="border rounded-md bg-background p-3 max-h-96 overflow-y-auto">
                {loading ? (
                  <p className="text-sm text-muted-foreground">Loading target profiles...</p>
                ) : (currentProfiles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No target profiles found for this audience.</p>
                ) : (
                  <div className="space-y-2">
                    {renderTargetProfileTree(currentProfiles)}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-md border bg-background p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Plus className="h-4 w-4" /> Create New {selectedAudience} Profile
                </h3>
                <form className="space-y-4" onSubmit={handleCreateSubmit}>
                  <div className="space-y-2">
                    <Label htmlFor="new-profile-name">Name</Label>
                    <Input
                      id="new-profile-name"
                      value={createForm.name}
                      onChange={event => setCreateForm(prev => ({ ...prev, name: event.target.value }))}
                      placeholder="Enter target profile name"
                      disabled={loading || isCreating}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-profile-description">Description (optional)</Label>
                    <Textarea
                      id="new-profile-description"
                      value={createForm.description}
                      onChange={event => setCreateForm(prev => ({ ...prev, description: event.target.value }))}
                      placeholder="Describe this target audience"
                      rows={3}
                      disabled={loading || isCreating}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Parent Profile (optional)</Label>
                    <Select
                      value={createForm.parentId != null ? createForm.parentId.toString() : 'none'}
                      onValueChange={value =>
                        setCreateForm(prev => ({
                          ...prev,
                          parentId: value === 'none' ? null : Number(value)
                        }))
                      }
                      disabled={loading || isCreating || flattenedParentOptions.length === 0}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select parent profile" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No parent (top level)</SelectItem>
                        {flattenedParentOptions.map(option => (
                          <SelectItem key={option.id} value={option.id.toString()}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {createError && (
                    <Alert variant="destructive">
                      <AlertTitle>Unable to create profile</AlertTitle>
                      <AlertDescription>{createError}</AlertDescription>
                    </Alert>
                  )}
                  <CapabilityButton capability={P.CampaignTargetProfileCreate} type="submit" disabled={isCreating} className="w-full">
                    {isCreating ? 'Creating...' : 'Create Profile'}
                  </CapabilityButton>
                </form>
              </div>

              <div className="rounded-md border bg-background p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Edit Selected Profile</h3>
                {selectedProfile ? (
                  <form className="space-y-4" onSubmit={handleUpdateSubmit}>
                    <div className="space-y-2">
                      <Label htmlFor="edit-profile-name">Name</Label>
                      <Input
                        id="edit-profile-name"
                        value={editForm.name}
                        onChange={event => setEditForm(prev => ({ ...prev, name: event.target.value }))}
                        disabled={isUpdating || isDeleting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-profile-description">Description</Label>
                      <Textarea
                        id="edit-profile-description"
                        value={editForm.description}
                        onChange={event => setEditForm(prev => ({ ...prev, description: event.target.value }))}
                        rows={3}
                        disabled={isUpdating || isDeleting}
                      />
                    </div>
                    {selectedProfile.children && selectedProfile.children.length > 0 && (
                      <Alert>
                        <AlertTitle>Child profiles</AlertTitle>
                        <AlertDescription>
                          This profile has {selectedProfile.children.length} direct child profile{selectedProfile.children.length === 1 ? '' : 's'}. Removing it requires deleting or reassigning its children first.
                        </AlertDescription>
                      </Alert>
                    )}
                    {editError && (
                      <Alert variant="destructive">
                        <AlertTitle>Unable to save changes</AlertTitle>
                        <AlertDescription>{editError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      <CapabilityButton capability={P.CampaignTargetProfileUpdate} type="submit" disabled={isUpdating || isDeleting}>
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                      </CapabilityButton>
                      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                        <AlertDialogTrigger asChild>
                          <Button
                            type="button"
                            variant="destructive"
                            disabled={isDeleting}
                            className="flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" /> Delete Profile
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete target profile</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. The profile "{selectedProfile.name}" will be permanently removed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                            <CapabilityButton capability={P.CampaignTargetProfileDelete} component={AlertDialogAction}
                              onClick={handleDeleteProfile}
                              disabled={isDeleting}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </CapabilityButton>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </form>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Select a target profile from the list to edit its details or remove it.
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
